import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import Select from 'react-select';
import { parse } from 'qs';
import JobsListBaseComponent from '../../common/JobsListBaseComponent';
import { getAllJobs, cancelJobAction } from '../../../actions/jobs';
import { getCompanyEvents, getEventDetails } from '../../../actions/events';
import { searchCompaniesByName, getCompanyDetailsAction, clearCompaniesSearchByNameResults } from '../../../actions/companies';
import { getAllSkills } from '../../../actions/skills';
import { jobsListPeriods } from '../../../constants/jobs';
import UsersService from '../../../services/UsersService';
import { extractDataFromSearch, getSelectedFilterValuesForUI } from '../../../helpers/general';
import { stringifyJobsFilterParams, splitJobsByDate } from '../../../helpers/jobs';
import { setDateFromUrlParam } from '../../../helpers/date';
import StatusFilter from './StatusFilter';
import AdvancedFilter from '../../common/AdvancedFilter';
import JobListSection from './JobListSection';
import EmptyListUi from './EmptyListUi';
import Loader from '../../common/ui/Loader';
import Close from '../../common/ui/Close';
import { UIShowConfirm } from '../../../actions/ui';
import FilterJobs from '../../common/AdvancedFilter/FilterJobs';
import AdvancedFilterButton from '../../common/AdvancedFilter/AdvancedFilterButton';
import ButtonRounded from '../../common/forms/buttons/ButtonRounded';
import SearchCriteria from '../../common/AdvancedFilter/SearchCriteria';

const emptyFilter = {
  company_id: null,
  event: null,
  skills: [],
  start_date_from: null,
  start_date_to: null,
  invoice_date_from: null,
  invoice_date_to: null
};

class JobsList extends JobsListBaseComponent {
  static propTypes = {
    jobsList: PropTypes.array.isRequired,
    pagesQuantity: PropTypes.number.isRequired,
    companiesData: PropTypes.array.isRequired,
    companyEvents: PropTypes.array.isRequired,
    eventDetails: PropTypes.object.isRequired,
    skillsList: PropTypes.array.isRequired,
    pastJobsWarning: PropTypes.bool.isRequired,
    getDataAction: PropTypes.func.isRequired,
    clearCompaniesSearchByNameResults: PropTypes.func.isRequired,
    searchCompaniesByName: PropTypes.func.isRequired,
    getCompanyDetailsAction: PropTypes.func.isRequired,
    getAllSkills: PropTypes.func.isRequired,
    getCompanyEvents: PropTypes.func.isRequired,
    getEventDetails: PropTypes.func.isRequired,
    cancelJobAction: PropTypes.func.isRequired,
    UIShowConfirm: PropTypes.func.isRequired
  };

  constructor (props) {
    super(props);

    const query = parse(window.location.search, { ignoreQueryPrefix: true });
    const { get_not_completed, statuses, company_id, event_id, skills, start_date_from, start_date_to, invoice_date_from, invoice_date_to, ...queryString } = query;
    const filteringValues = {
      company_id,
      event: event_id ? {
        value: event_id,
        label: ''
      } : null,
      skills: skills || [],
      start_date_from: setDateFromUrlParam(start_date_from),
      start_date_to: setDateFromUrlParam(start_date_to),
      invoice_date_from: setDateFromUrlParam(invoice_date_from),
      invoice_date_to: setDateFromUrlParam(invoice_date_to)
    };
    this.state = {
      ...queryString,
      activePage: this.getActivePage(),
      period: this.getSelectedPeriod(),
      statuses: statuses || [],
      get_not_completed: !!get_not_completed || false,
      filteringValues: filteringValues,
      appliedFilterParams: getSelectedFilterValuesForUI(filteringValues, emptyFilter),
      goToPageBtnIsShown: false,
      showFilter: false,
      isLoading: true
    };

    this.handleCompaniesInput = _.debounce(this.handleCompaniesInput, 500);
  }

  componentDidMount () {
    Promise.all([
      this._loadData(this.state.activePage),
      this.props.getAllSkills()
    ]).then(this._setDataLoaded);
    const { filteringValues } = this.state;
    if (filteringValues.company_id) {
      this.props.getCompanyDetailsAction(filteringValues.company_id);
      this.getCompanyEvents();
    }

    if (filteringValues.event) {
      this.props.getEventDetails(filteringValues.event.value).then(() => {
        const updatedFilteringValues = {
          ...this.state.filteringValues,
          event: {
            ...this.state.filteringValues.event,
            label: this.props.eventDetails.name
          }
        };
        this.setState({
          filteringValues: updatedFilteringValues,
          appliedFilterParams: getSelectedFilterValuesForUI(updatedFilteringValues, emptyFilter)
        });
      });
    }
  }

  _loadData (activePage) {
    this._setDataIsLoading();
    super.loadData(activePage, stringifyJobsFilterParams(this.state))
      .then(this._setDataLoaded);
  };

  selectStatus = status => {
    let { statuses } = this.state;
    const index = statuses.indexOf(status);
    if (index === -1) {
      statuses = [status];
    } else {
      statuses = [];
    }
    this.setState({
      statuses,
      get_not_completed: false,
      activePage: 0
    }, () => this._loadData(0));
  };

  toggleNonCompleted = () => {
    this.setState(prevState => ({
      get_not_completed: !prevState.get_not_completed,
      statuses: [],
      activePage: 0
    }), () => this._loadData(0));
  };

  handleCompaniesInput = input => {
    if (input.length >= 3) {
      this.props.searchCompaniesByName(input);
    }
  };

  handleChangeCompany = option => {
    const company_id = option ? option.value : emptyFilter['company_id'];
    this.setState({
      filteringValues: {
        ...this.state.filteringValues,
        company_id
      }
    }, () => {
      if (option) {
        this.getCompanyEvents();
      } else {
        this.handleChange('event', emptyFilter['event']);
        this.props.clearCompaniesSearchByNameResults();
      }
    });
    this.handleChange('company_id', company_id).then(this.applyFilter);
  };

  handleChange = async (name, value) => {
    this.setState({
      filteringValues: {
        ...this.state.filteringValues,
        [name]: value
      }
    });
  };

  applyFilter = () => {
    const { filteringValues } = this.state;
    this.setState({
      appliedFilterParams: getSelectedFilterValuesForUI(filteringValues, emptyFilter)
    });
    this.closeFilter();
    this._loadData(0);
  };

  handleClearFilterParam = name => {
    this.handleChange(name, emptyFilter[name]).then(this.applyFilter);
  };

  handleClearMultipleFilterParam = (name, filterParam) => {
    let multipleValues = this.state.filteringValues[name];

    if (multipleValues.some(item => item.value === filterParam.value)) {
      multipleValues = multipleValues.filter(item => item.value !== filterParam.value);
    } else {
      multipleValues.push(filterParam);
    }

    return this.handleChange(name, multipleValues).then(this.applyFilter);
  };

  clearFilter = () => {
    this.setState({ filteringValues: emptyFilter }, () => this.applyFilter());
  };

  _renderCompanySelect = (onChange = this.handleChangeCompany) => {
    const { companiesData, clearCompaniesSearchByNameResults } = this.props;
    const { filteringValues } = this.state;
    return (
      <Select
        onChange={onChange}
        onInputChange={this.handleCompaniesInput}
        options={companiesData.map(company => ({ label: company.company_name, value: company.id }))}
        value={filteringValues.company_id}
        placeholder={'Enter Company Name'}
        noResultsText={null}
        clearRenderer={() => <Close className='button-clear' />}
        onBlur={filteringValues.company_id ? null : clearCompaniesSearchByNameResults}
      />
    );
  };

  openFilter = () => this.setState({ showFilter: true });

  closeFilter = () => this.setState({ showFilter: false });

  getCompanyEvents = () => this.props.getCompanyEvents(this.state.filteringValues.company_id);

  render () {
    const { period, statuses, get_not_completed, showFilter, isLoading, filteringValues, appliedFilterParams } = this.state;
    const { company_id } = filteringValues;
    const { pagesQuantity, companyEvents, skillsList } = this.props;

    const jobsListSectionsByDate = splitJobsByDate(this.sortJobsList());
    const jobsListExists = jobsListSectionsByDate.length > 0;
    const advancedFilterIsApplied = appliedFilterParams && appliedFilterParams.length > 0;
    const statusesFilterIsApplied = statuses.length > 0 || get_not_completed;
    const filtersAreApplied = advancedFilterIsApplied || statusesFilterIsApplied;
    const needToShowFilters = jobsListExists || filtersAreApplied || company_id;
    const isCurrentJobsList = period === jobsListPeriods.current.id;
    const isPastJobsList = period === jobsListPeriods.past.id;
    const hasEvents = !!(company_id && companyEvents.length);

    return (
      <div className={`job-list-wrapper ${isLoading ? 'is-loading' : ''}`}>
        <div className='tabs-wrapper job-list-page'>
          <div className='tabs-wrapper-job-list'>
            { this.renderTabs() }
          </div>
          <div className='d-flex plus-filter-wrapper'>
            <ButtonRounded onClick={this.navigateToCreateJob} />
            { needToShowFilters && (
              <AdvancedFilterButton
                onClick={this.openFilter}
                isFilterApplied={advancedFilterIsApplied}
              />
            )}
          </div>
        </div>
        { needToShowFilters && (
          <React.Fragment>
            <AdvancedFilter
              showFilter={showFilter}
              applyFilter={this.applyFilter}
              closeFilter={this.closeFilter}
              handleChange={this.handleChange}
              skillsList={skillsList}
              hasEvents={hasEvents}
              companyEvents={companyEvents}
              clearFilter={this.clearFilter}
              Layout={FilterJobs}
              {...filteringValues}
            />
            { UsersService.isAdmin() && (
              <div className='mt-4 mb-3'>
                {this._renderCompanySelect()}
              </div>
            )}
            <SearchCriteria
              appliedFilterParams={appliedFilterParams}
              handleClearMultipleFilterParam={this.handleClearMultipleFilterParam}
              handleClearFilterParam={this.handleClearFilterParam}
            />
            <StatusFilter
              isCurrentPeriod={isCurrentJobsList}
              get_not_completed={get_not_completed}
              selectStatus={this.selectStatus}
              statuses={statuses}
              toggleNonCompleted={this.toggleNonCompleted}
            />
          </React.Fragment>
        )}
        { isLoading ? <div className='loader-wrapper'><Loader /></div> : (
          <React.Fragment>
            { jobsListExists ? (
              <div>
                {jobsListSectionsByDate.map(section => (
                  <JobListSection
                    key={section.formattedDate}
                    formattedDate={section.formattedDate}
                    weekDay={section.weekDay}
                    jobsList={section.data}
                    history={this.props.history}
                    cancelJob={this.cancelJob}
                    isPastJobsList={isPastJobsList}
                    UIShowConfirm={this.props.UIShowConfirm}
                  />
                ))}
                { pagesQuantity > 1 && this.renderPagination() }
              </div>
            ) : (
              <EmptyListUi
                filtersAreApplied={filtersAreApplied}
                isCurrentJobsList={isCurrentJobsList}
                navigateToCreateJob={this.navigateToCreateJob}
              />
            )}
          </React.Fragment>
        )}
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    jobsList: state.jobs.jobsList,
    pagesQuantity: state.jobs.pagesQuantity,
    companiesData: extractDataFromSearch(state.companies.searchResults, state.companies.companyDetails),
    companyEvents: state.companies.companyDetails.events,
    eventDetails: state.events.eventDetails,
    skillsList: state.skillsList,
    pastJobsWarning: state.jobs.pastJobsWarning
  };
};

const mapDispatchToProps = {
  getDataAction: getAllJobs,
  searchCompaniesByName,
  clearCompaniesSearchByNameResults,
  getCompanyDetailsAction,
  getAllSkills,
  getCompanyEvents,
  getEventDetails,
  cancelJobAction,
  UIShowConfirm
};

export default connect(mapStateToProps, mapDispatchToProps)(JobsList);



// WEBPACK FOOTER //
// ./src/components/app/jobsList/index.js